import type { StateCreator } from 'zustand'

import type { AccountStore } from '@common/modules/settings/account/stores/types'
import type { ThemePreference } from '@common/modules/settings/types'
import { resolveThemePreference } from '@common/modules/settings/utils/resolveThemePreference'
import {
  getUserRegionalData,
  updateUserAvatar,
  updateUserEmail,
  updateUserNames,
} from '@common/modules/user/regional-user'
import { deleteUser } from '@common/modules/user/user'

import type { AccountActionsSlice } from './account-actions.slice.type'

export const createAccountActionsSlice: StateCreator<
  AccountStore,
  [['zustand/immer', never]],
  [],
  AccountActionsSlice
> = (set) => ({
  getAccountProfile: async () => {
    set((state: AccountStore) => {
      state.loading = true
      state.error = null
    })
    try {
      const user = await getUserRegionalData()
      if (!user) throw new Error('Failed to fetch account data')
      set((state: AccountStore) => {
        state.profile = {
          id: user.id,
          firstName: user.firstname,
          lastName: user.lastname,
          email: user.email,
          picture: user.avatarUrl,
          createdAt: '',
        }
        state.signInMethods = []
        state.twoFactorStatus = { enabled: false, method: null }
      })
    } catch (err) {
      set((state: AccountStore) => {
        state.error = err instanceof Error ? err.message : 'Failed to fetch account data'
      })
    } finally {
      set((state: AccountStore) => {
        state.loading = false
      })
    }
  },

  updateFullName: async (firstName: string, lastName: string) => {
    set((state: AccountStore) => {
      state.loading = true
      state.error = null
    })
    try {
      await updateUserNames(firstName, lastName)
      set((state: AccountStore) => {
        if (state.profile) {
          state.profile.firstName = firstName
          state.profile.lastName = lastName
        }
      })
      return true
    } catch (err) {
      set((state: AccountStore) => {
        state.error = err instanceof Error ? err.message : 'Failed to update name'
      })
      return false
    } finally {
      set((state: AccountStore) => {
        state.loading = false
      })
    }
  },

  updateEmail: async (email: string) => {
    set((state: AccountStore) => {
      state.loading = true
      state.error = null
    })
    try {
      await updateUserEmail(email)
      set((state: AccountStore) => {
        if (state.profile) {
          state.profile.email = email
        }
      })
      return true
    } catch (err) {
      set((state: AccountStore) => {
        state.error = err instanceof Error ? err.message : 'Failed to update email'
      })
      return false
    } finally {
      set((state: AccountStore) => {
        state.loading = false
      })
    }
  },

  updateProfilePicture: async (picture: number[]) => {
    set((state: AccountStore) => {
      state.loading = true
      state.error = null
    })
    try {
      await updateUserAvatar(picture)
      const user = await getUserRegionalData()
      if (!user) throw new Error('Failed to refresh profile after avatar update')
      set((state: AccountStore) => {
        state.profile = {
          id: user.id,
          firstName: user.firstname,
          lastName: user.lastname,
          email: user.email,
          picture: user.avatarUrl,
          createdAt: '',
        }
        state.signInMethods = []
        state.twoFactorStatus = { enabled: false, method: null }
      })
      return true
    } catch (err) {
      set((state: AccountStore) => {
        state.error = err instanceof Error ? err.message : 'Failed to update profile picture'
      })
      return false
    } finally {
      set((state: AccountStore) => {
        state.loading = false
      })
    }
  },

  // TODO: implement when UserService exposes UpdateAccountPassword
  updatePassword: async () => {
    set((state: AccountStore) => {
      state.error = 'Not implemented'
    })
  },

  // TODO: implement when UserService exposes UnlinkAuthProvider
  unlinkProvider: async () => {
    set((state: AccountStore) => {
      state.error = 'Not implemented'
    })
  },

  // TODO: implement when UserService exposes ConfigureTwoFactor
  configureTwoFactor: async () => {
    set((state: AccountStore) => {
      state.error = 'Not implemented'
    })
  },

  setThemePreference: (theme: ThemePreference) => {
    resolveThemePreference.save(theme)
    set((state: AccountStore) => {
      state.themePreference = theme
    })
  },

  deleteAccount: async () => {
    set((state: AccountStore) => {
      state.loading = true
      state.error = null
    })
    try {
      await deleteUser()
      return true
    } catch (err) {
      set((state: AccountStore) => {
        state.error = err instanceof Error ? err.message : 'Failed to delete account'
      })
      return false
    } finally {
      set((state: AccountStore) => {
        state.loading = false
      })
    }
  },

  clearError: () => {
    set((state: AccountStore) => {
      state.error = null
    })
  },
})
